/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA PAGINATION PAR CURSEUR RENCONTRE UN CADRE QUI COMPTE EN PAGES.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `DOCTRINE.md` §5 est catégorique, et son motif est écrit : « **pagination par curseur,
 * jamais par numéro de page** : un décalage devient faux dès qu'une ligne est insérée
 * pendant la consultation, et son coût croît avec la profondeur ». Les trois listes de
 * cette surface la respectent — elles rendent `{ …, suite: "<opaque> | null" }`.
 *
 * `ra-core`, lui, demande `{ pagination: { page, perPage } }`. **Ce n'est pas un défaut de
 * l'un ni de l'autre** : ce sont deux conventions, et quelque chose doit les joindre. Ce
 * fichier est ce quelque chose, et il est **le seul endroit** où le numéro de page existe.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUI EST REFUSÉ, ET POURQUOI CE N'EST PAS UN CONFORT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * La tentation serait de traduire `page` en décalage — `?offset=(page-1)*perPage`. Elle est
 * fermée deux fois : **le contrat ne porte aucun paramètre de décalage**, donc le client
 * généré n'a pas la fonction, et `DOCTRINE.md` §4 interdit d'écrire l'appel à la main. « Un
 * défaut de contrat ne se paie pas en confort, il se paie en contournement » (D-068) — ici
 * il n'y a pas de défaut, et donc pas de contournement à écrire.
 *
 * **Ce qu'on fait à la place** : on retient le curseur que chaque tranche rend, et on
 * n'avance que d'un cran à la fois. `GetListResult` de `ra-core` accepte `pageInfo` **au
 * lieu de** `total`, prévu exactement pour cela : la peau rend alors « précédent /
 * suivant » et **aucun numéro de page**, ce qui est la vérité de ce que l'API sait faire.
 *
 * **Aucun total n'est donc affiché, et c'est juste.** Le contrat n'en rend aucun sur les
 * trois listes, et en inventer un — par un comptage côté client, par exemple — donnerait un
 * nombre faux sur la première insertion. `CDC-5` ne demande de total nulle part : ce qu'il
 * demande est **la somme des minutes**, qui vient du serveur avec la tranche.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LA MÉMOIRE EST PAR LISTE ET PAR FILTRE, ET ELLE SE JETTE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Changer un filtre change la suite : le curseur de la page 2 d'une recherche ne vaut rien
 * pour une autre. La signature du filtre fait donc partie de la clef, et une recherche
 * repart de la première tranche. **C'est aussi ce qui empêche le pire défaut possible ici**
 * — servir la tranche d'un autre filtre en croyant paginer, ce qui ne planterait pas et
 * afficherait simplement les mauvaises lignes.
 *
 * Cette mémoire vit **en variable de module**, pas dans un magasin : elle est jetable,
 * reconstructible, et n'a aucun intérêt à survivre à un rechargement — F-55 « entrer dans
 * la surface et parcourir une liste ne laissent **aucune ligne de trace** », et un curseur
 * conservé serait la trace d'un parcours.
 */

/** Ce qu'une tranche laisse derrière elle pour que la suivante se demande. */
type Memoire = {
  /** Le curseur à présenter pour obtenir la page N. `undefined` en page 1. */
  readonly curseurs: Map<number, string | undefined>;
  /** La dernière page dont on a vu la réponse, pour savoir s'il en reste. */
  derniereVue: number;
  /** Vrai quand la dernière tranche vue portait un curseur de suite. */
  ilEnReste: boolean;
};

const memoires = new Map<string, Memoire>();

/**
 * **La clef d'une liste : sa ressource et son filtre.**
 *
 * `JSON.stringify` sur un objet ne garantit pas l'ordre des clefs entre deux appels — il
 * suit l'ordre d'insertion. Deux filtres identiques construits dans un ordre différent
 * donneraient donc deux clefs, deux mémoires, et une pagination qui repart de zéro sans
 * raison visible. Les clefs sont triées pour cela.
 */
export function clefDeListe(ressource: string, filtre: Record<string, unknown>): string {
  const entrees = Object.entries(filtre)
    .filter(([, valeur]) => valeur !== undefined && valeur !== null && valeur !== '')
    .sort(([a], [b]) => a.localeCompare(b));
  return `${ressource}::${JSON.stringify(entrees)}`;
}

/**
 * **Le curseur pour la page demandée, ou le refus de la servir.**
 *
 * Rend `{ curseur }` quand on sait la demander. Rend `null` quand on ne sait pas — un saut
 * direct en page 5 sans avoir vu les précédentes. **On refuse au lieu de deviner** : servir
 * la page 1 en croyant servir la page 5 est un défaut silencieux, et c'est celui-là qui
 * coûte cher. La peau ne propose de toute façon que « suivant » et « précédent », donc ce
 * refus ne se rencontre que sur une adresse forgée à la main.
 */
export function curseurPour(
  clef: string,
  page: number,
): { readonly curseur: string | undefined } | null {
  if (page <= 1) return { curseur: undefined };
  const memoire = memoires.get(clef);
  if (memoire === undefined) return null;
  if (!memoire.curseurs.has(page)) return null;
  return { curseur: memoire.curseurs.get(page) };
}

/** Ce qu'on retient d'une tranche servie : le curseur de la suivante, s'il y en a une. */
export function retenirLaTranche(clef: string, page: number, suite: string | null): void {
  const memoire =
    memoires.get(clef) ??
    { curseurs: new Map<number, string | undefined>(), derniereVue: 0, ilEnReste: false };
  if (suite !== null) memoire.curseurs.set(page + 1, suite);
  memoire.derniereVue = page;
  memoire.ilEnReste = suite !== null;
  memoires.set(clef, memoire);
}

/**
 * **Ce que la peau affiche : deux booléens, jamais un nombre de pages.**
 *
 * `hasNextPage` vient du curseur rendu par le serveur — c'est **lui** qui sait s'il reste
 * quelque chose, et c'est la seule source qui ne se trompe pas. `hasPreviousPage` est
 * arithmétique : la page 1 n'a pas de précédente.
 */
export function informationDePage(page: number, suite: string | null) {
  return { hasNextPage: suite !== null, hasPreviousPage: page > 1 };
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE TOTAL EXACT QUAND IL EST CONNU SANS COMPTER — ET SEULEMENT ALORS.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Le défaut que ceci ferme, vu en recette le 20260825.** Le pied de toutes les listes
 * affichait ses gabarits en clair — `1-%{offsetEnd} sur %{total}`. Le libellé français
 * existait bien : c'est **l'interpolation** qui échouait, faute de valeurs.
 *
 * `list-pagination.tsx`, fichier de peau qu'on ne retouche pas, calcule
 * `pageEnd = hasNextPage ? page * perPage : total`. **Sur la dernière page il lit donc
 * `total`**, que la pagination par curseur ne fournit pas — `DOCTRINE.md` §5 l'a écartée
 * pour n'avoir aucun `COUNT`. Polyglot laisse alors ses `%{...}` intacts.
 *
 * **CE N'EST PAS UN RENVERSEMENT DE §5, ET LA DISTINCTION EST TOUT.** Sur la dernière page
 * — celle où il n'y a pas de suite —, le total **est déductible de ce qu'on a déjà** :
 * `(page - 1) * parPage + reçues`. **Aucune requête de plus, aucun `COUNT`** : c'est de
 * l'arithmétique sur une réponse en main. §5 refuse de **compter la table**, pas de savoir
 * additionner.
 *
 * **Et il rend `undefined` dès qu'il y a une suite**, ce qui est le point : le nombre de
 * lignes au-delà du curseur est **inconnaissable sans compter**. Rendre une estimation, ou
 * le total de la page courante, afficherait un chiffre faux avec l'aplomb d'un vrai — le
 * seul défaut pire qu'un gabarit en clair.
 */
export function totalSiConnu(
  page: number,
  parPage: number,
  recues: number,
  suite: string | null,
): number | undefined {
  return suite === null ? (page - 1) * parPage + recues : undefined;
}

/**
 * **Oublier ce qu'on savait d'une liste.** Appelée après une écriture qui la change — un
 * rattachement créé, un cours masqué : les curseurs d'après portent sur un ordre qui a
 * bougé, et les garder ferait sauter ou répéter des lignes.
 */
export function oublier(ressource: string): void {
  for (const clef of [...memoires.keys()]) {
    if (clef.startsWith(`${ressource}::`)) memoires.delete(clef);
  }
}
